import { Flag, CalendarClock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useFollowUpTimeEnabled } from "@/features/leads/api/useLeadFormConfig";

/**
 * Compact "what's next" row used inside the lead grid card.
 *
 * Replaces the legacy "Course" row. Priority is:
 *   1. Pending deadline (milestone) → flag pill, optionally amber when past nudge
 *   2. Pending follow-up → calendar pill with relative date
 *   3. Empty placeholder so the card height stays consistent
 *
 * The row stays a single line and uses the same icon + text rhythm as the
 * other body rows (Email / Phone / Branch) so the card layout doesn't shift.
 */
export type NextDeadlineLite = {
  id: number;
  status: string;
  warningDays: number | null;
  triggeredAt: string | null;
  createdAt: string;
  fromStatusName: string | null;
  toStatusName: string | null;
};

export type NextFollowUpLite = {
  id: number;
  date: string;
  status: string;
};

interface NextUpRowProps {
  deadline: NextDeadlineLite | null;
  followUp: NextFollowUpLite | null;
}

function calendarDaysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const start = new Date(iso);
  if (Number.isNaN(start.getTime())) return null;
  start.setHours(0, 0, 0, 0);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((now.getTime() - start.getTime()) / 86400000));
}

/**
 * Relative label + tone for a follow-up date, calendar-day based. EXPORTED so
 * the drawer's Followups tab prints exactly what the card chip prints for the
 * same date (one wording for "12 days overdue" everywhere).
 */
export function formatRelativeFollowUp(iso: string): {
  label: string;
  tone: "overdue" | "today" | "soon" | "later";
} {
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return { label: "—", tone: "later" };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const t = new Date(target);
  t.setHours(0, 0, 0, 0);
  const diffDays = Math.round(
    (t.getTime() - today.getTime()) / 86400000
  );
  if (diffDays < 0) {
    const abs = Math.abs(diffDays);
    return {
      label: abs === 1 ? "1 day overdue" : `${abs} days overdue`,
      tone: "overdue",
    };
  }
  if (diffDays === 0) return { label: "Today", tone: "today" };
  if (diffDays === 1) return { label: "Tomorrow", tone: "soon" };
  if (diffDays <= 7) return { label: `In ${diffDays} days`, tone: "soon" };
  return {
    label: target.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
    }),
    tone: "later",
  };
}

export default function NextUpRow({ deadline, followUp }: NextUpRowProps) {
  // Reads the shared, cached `/form-config` query — one network request for the
  // whole app however many cards render this row.
  const followUpTimeEnabled = useFollowUpTimeEnabled();

  if (deadline) {
    const target = deadline.toStatusName || "Milestone";
    const wd = deadline.warningDays;
    const elapsed = calendarDaysSince(deadline.triggeredAt);
    const armed = deadline.triggeredAt != null && elapsed != null;
    const pastNudge = armed && wd != null && (elapsed as number) >= wd;

    const counter =
      armed && wd != null
        ? `${elapsed}/${wd}d`
        : wd != null
          ? `${wd}d`
          : null;

    return (
      <Tooltip delayDuration={250}>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <Flag
              width={18}
              height={18}
              strokeWidth={1.75}
              className={
                pastNudge
                  ? "text-[#b45309] shrink-0"
                  : "text-[var(--color-success)] shrink-0"
              }
            />
            <span
              className={`text-b4-mid font-normal leading-[1.35] whitespace-nowrap overflow-hidden text-ellipsis text-[14px] ${
                pastNudge ? "text-[#92400e]" : "text-[#1e302a]"
              }`}
              title={`Milestone target: ${target}`}
            >
              <span className="font-medium">Deadline</span>
              <span className="text-[#697571]"> · </span>
              <span>{target}</span>
              {counter ? (
                <span
                  className={`ml-1.5 inline-flex items-center px-1.5 py-[1px] rounded-[6px] text-[11.5px] font-medium tabular-nums ${
                    pastNudge
                      ? "bg-amber-100 text-[#92400e]"
                      : "bg-[var(--color-black-2)]/50 text-[var(--color-black-7)]"
                  }`}
                >
                  {counter}
                </span>
              ) : null}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className="max-w-[260px]">
          <div className="text-[12px] leading-snug">
            <div className="font-semibold mb-0.5">Milestone</div>
            <div className="text-[var(--color-black-7)]">
              Auto-completes when the lead reaches{" "}
              <span className="font-medium text-[var(--color-text-primary)]">
                {target}
              </span>
              {deadline.fromStatusName ? (
                <>
                  {" "}from{" "}
                  <span className="font-medium text-[var(--color-text-primary)]">
                    {deadline.fromStatusName}
                  </span>
                </>
              ) : null}
              .
            </div>
            {wd != null ? (
              <div className="mt-1 text-[var(--color-black-6)]">
                {armed
                  ? `Day ${elapsed} of ${wd} since timer started`
                  : `Timer arms once the lead reaches the from-status (${wd}d nudge)`}
              </div>
            ) : null}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (followUp) {
    const { label, tone } = formatRelativeFollowUp(followUp.date);
    const colorClass =
      tone === "overdue"
        ? "text-[#b42318]"
        : tone === "today"
          ? "text-[var(--color-success)]"
          : tone === "soon"
            ? "text-[#1e302a]"
            : "text-[#1e302a]";
    return (
      <Tooltip delayDuration={250}>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <CalendarClock
              width={18}
              height={18}
              strokeWidth={1.75}
              className={
                tone === "overdue"
                  ? "text-[#b42318] shrink-0"
                  : "text-[var(--color-black-8)] shrink-0"
              }
            />
            <span
              className={`text-b4-mid font-normal leading-[1.35] whitespace-nowrap overflow-hidden text-ellipsis text-[14px] ${colorClass}`}
            >
              <span className="font-medium">Follow-up</span>
              <span className="text-[#697571]"> · </span>
              <span>{label}</span>
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className="max-w-[240px]">
          <div className="text-[12px] leading-snug">
            <div className="font-semibold mb-0.5">Next follow-up</div>
            <div className="text-[var(--color-black-7)]">
              {new Date(followUp.date).toLocaleString("en-IN", {
                weekday: "short",
                day: "2-digit",
                month: "short",
                year: "numeric",
                // A date-only tenant stores 00:00; quoting it as "12:00 AM"
                // would invent a time nobody picked.
                ...(followUpTimeEnabled
                  ? { hour: "2-digit" as const, minute: "2-digit" as const }
                  : {}),
              })}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <div className="flex items-center gap-1.5 flex-1 min-w-0">
      <Flag
        width={18}
        height={18}
        strokeWidth={1.75}
        className="text-[#c4c8c6] shrink-0"
      />
      <span className="text-b4-mid font-normal leading-[1.35] whitespace-nowrap overflow-hidden text-ellipsis text-[14px] text-gray-400 italic">
        No deadline or follow-up
      </span>
    </div>
  );
}
